/**
 * Step 26 Phase B — SO-grouped Fulfillment View (production + purchase modes).
 *
 * Renders SOs as collapsible cards. Each SO carries:
 *   - mode="production": line items → WOs → processes (with JC actions)
 *   - mode="purchase":   line items + indent requirements (with Create Indent + view links)
 *
 * Key behaviours per locked decisions (Phase A):
 *   - SOs whose status ∈ {cancelled, deleted, dispatched} are filtered server-side.
 *   - Fully-fulfilled / dispatched line items render dimmed with "Dispatched" tag.
 *   - Combined-SFG / Combined-PO render under EVERY parent SO with sibling chips
 *     (max 3 visible + "+N more" tooltip).
 *   - Stock indents/POs are excluded by the backend.
 *   - Per-SO production rollup is share-weighted; purchase rollup is line-count.
 *   - Action buttons reuse existing forms only:
 *       Production: Issue JC / Receive Against Process / View Progress →
 *                   deep-link to WorkOrderPage with ?wo=&action=&process=&job_id=
 *       Purchase:   Create Indent → opens IndentCreationDialog in-place
 *                   View Indent / View PO → simple navigate to register pages
 *   - Cancelled production plans render struck-through with the cancellation reason.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  ChevronRight, ChevronDown, Search, Layers, CircleDashed, CheckCircle2,
  Clock, AlertTriangle, FileText, ExternalLink, RefreshCw, FileDown,
} from 'lucide-react';
import IndentCreationDialog from '../../components/IndentCreationDialog';
import { exportToExcel } from '../../utils/exportToExcel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const SIBLING_CHIP_LIMIT = 3;

const getStatusPillClasses = (s) => {
  // Generic helpers for compact status pills.
  const v = (s || '').toLowerCase();
  if (['completed', 'received', 'fully_fulfilled', 'closed'].includes(v)) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (['in_progress', 'partial', 'partially_completed', 'partially_dispatched', 'in_production'].includes(v)) return 'bg-amber-100 text-amber-700 border-amber-200';
  if (['cancelled', 'deleted', 'rejected'].includes(v)) return 'bg-rose-100 text-rose-700 border-rose-200';
  if (['issued', 'po_raised', 'po_created', 'awaiting_approval', 'pending_approval', 'clarification_requested'].includes(v)) return 'bg-blue-100 text-blue-700 border-blue-200';
  if (['confirmed', 'created', 'planned'].includes(v)) return 'bg-slate-100 text-slate-700 border-slate-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toISOString().slice(0, 10);
  } catch {
    return '—';
  }
};

// =====================================================================
// Pending Production tab — helpers
// =====================================================================
//
// PENDING_PRODUCTION_TAB is a sentinel value used in the same Tabs control
// as the auto-derived SO-status tabs. Picking it switches the body from the
// SO-accordion view to a flat per-line table. Real SO-status strings never
// collide with this sentinel because they're stored lowercase enum keys.
const PENDING_PRODUCTION_TAB = 'pending_production';

// Returns an ISO YYYY-MM-DD string for the SO's target production date.
//   1) Prefer the stored target_production_date (mandatory for modern SOs).
//   2) Fall back to delivery_date - 5 days (backend convention at
//      server.py:2157 and production_fulfillment.py:1012). Do NOT use the
//      frontend-print 3-day fallback at SOPrint.js:54 — that's a latent
//      inconsistency in the print path only.
//   3) If both are missing, return null. The sort places nulls last.
const resolveTargetProductionDate = (so) => {
  const stored = so?.target_production_date;
  if (stored) {
    const d = new Date(stored);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  const delivery = so?.delivery_date;
  if (delivery) {
    const d = new Date(delivery);
    if (!isNaN(d.getTime())) {
      d.setDate(d.getDate() - 5);
      return d.toISOString().slice(0, 10);
    }
  }
  return null;
};

// Human-readable line-level status for the Pending Production tab.
// 'Ready' is defined for completeness but never appears in this tab because
// fully-produced lines are filtered out before status derivation.
const derivePendingLineStatus = (line) => {
  const b = line?.badges || {};
  if (b.bom_pending) return 'Pending BOM';
  if (b.variant_pending) return 'Pending Variant';
  const pct = Number(line?.production_pct || 0);
  if (pct >= 100) return 'Ready';
  return 'In Progress';
};

// Compare two SO numbers by their embedded 4-digit sequence so 0001 sorts
// before 0108 regardless of letter prefix. Format is uniform "AAAAA9999A"
// (5 letters + 4 digits + 1 letter). Regex avoids hardcoding positions so
// shape changes don't silently miscompare. If either side fails the regex
// (shouldn't happen with current data), fall back to a full localeCompare
// so unparseable rows cluster predictably rather than landing arbitrarily.
// Final tie-break is a full localeCompare — defensive only; user confirmed
// no two SOs share the same 4-digit block.
const compareSoNumbers = (aSo, bSo) => {
  const aStr = aSo || '';
  const bStr = bSo || '';
  const aMatch = /\d{4}/.exec(aStr);
  const bMatch = /\d{4}/.exec(bStr);
  if (!aMatch || !bMatch) return aStr.localeCompare(bStr);
  const aNum = parseInt(aMatch[0], 10);
  const bNum = parseInt(bMatch[0], 10);
  if (aNum !== bNum) return aNum - bNum;
  return aStr.localeCompare(bStr);
};

// Build one flat row for the Pending Production table + Excel export.
// Object keys are exact column headers (json_to_sheet uses them verbatim).
// Mapper enumerates only the agreed columns — no financial fields can leak.
const buildPendingProductionRow = (so, line) => ({
  'SO Number': so.so_number || '',
  'Customer': so.customer_name || '',
  'Item Code': line.item_code || '',
  'Item Name': line.item_name || '',
  'Status': derivePendingLineStatus(line),
  'Delivery Date': formatDate(so.delivery_date),
  'Target Production Date': formatDate(resolveTargetProductionDate(so)),
  'Ordered Qty': line.required_qty || 0,
  'Produced Qty': line.produced_qty || 0,
  '% Produced': line.production_pct || 0,
  'BOM Pending': line?.badges?.bom_pending ? 'Yes' : 'No',
  'Variant Pending': line?.badges?.variant_pending ? 'Yes' : 'No',
});

// Pending Production column order — kept as a single source of truth so the
// rendered table headers and Excel column order cannot drift apart.
const PENDING_PRODUCTION_COLUMNS = [
  'SO Number', 'Customer', 'Item Code', 'Item Name', 'Status',
  'Delivery Date', 'Target Production Date',
  'Ordered Qty', 'Produced Qty', '% Produced',
  'BOM Pending', 'Variant Pending',
];

const tsStamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_` +
         `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
};

// =====================================================================
// Sibling SO chips component
// =====================================================================
const SiblingChips = ({ siblings = [] }) => {
  if (!siblings.length) return null;
  const visible = siblings.slice(0, SIBLING_CHIP_LIMIT);
  const overflow = siblings.length - visible.length;
  return (
    <span className="inline-flex flex-wrap items-center gap-1 ml-2">
      <span className="text-xs text-slate-500">shared with:</span>
      {visible.map((s) => (
        <Badge
          key={s}
          variant="outline"
          className="text-xs px-1.5 py-0 bg-violet-50 text-violet-700 border-violet-200"
          data-testid={`sibling-so-chip-${s}`}
        >
          {s}
        </Badge>
      ))}
      {overflow > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className="text-xs px-1.5 py-0 cursor-help bg-violet-50 text-violet-700 border-violet-200"
                data-testid="sibling-so-chip-overflow"
              >
                +{overflow} more
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-xs">{siblings.slice(SIBLING_CHIP_LIMIT).join(', ')}</div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </span>
  );
};

// =====================================================================
// Process row (production mode)
// =====================================================================
const ProcessRow = ({ proc, wo, onIssueJC, onReceive }) => {
  const status = proc.process_status_canonical;
  const isComplete = proc.is_complete;
  const isAtRisk = proc.is_at_risk;
  const planSummary = proc.plan_summary || {};
  const planHistory = proc.plan_history || [];
  const wo_qty = proc.wo_target_qty || 0;
  const completed = proc.completed_quantity || 0;
  const jobs = proc.jobs || [];
  const [showJCs, setShowJCs] = useState(false);

  const statusIcon = isComplete ? <CheckCircle2 size={14} className="text-emerald-600" /> :
    status === 'in_progress' ? <Clock size={14} className="text-amber-600" /> :
    status === 'cancelled' || status === 'rejected' ? <CircleDashed size={14} className="text-rose-500" /> :
    <CircleDashed size={14} className="text-slate-400" />;

  return (
    <div className="border-l-2 border-slate-100 pl-3 py-2" data-testid={`process-row-${proc.process_id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="flex items-center gap-1.5 min-w-0">
          {statusIcon}
          <span className="text-xs text-slate-400 font-mono">{proc.process_seq ?? '—'}</span>
          <span className="font-medium text-sm truncate">{proc.process_name}</span>
        </div>
        <Badge variant="outline" className={`text-xs px-1.5 py-0 ${getStatusPillClasses(status)}`}>
          {status?.replace(/_/g, ' ')}
        </Badge>
        <span className="text-xs text-slate-600 tabular-nums">
          {completed} / {wo_qty}
        </span>
        {isAtRisk && (
          <Badge variant="outline" className="text-xs px-1.5 py-0 bg-rose-50 text-rose-700 border-rose-200">
            <AlertTriangle size={11} className="mr-0.5" />at risk
          </Badge>
        )}
        {planSummary.current_delay_days_from_latest > 0 && (
          <Badge variant="outline" className="text-xs px-1.5 py-0 bg-rose-50 text-rose-700 border-rose-200">
            +{planSummary.current_delay_days_from_latest}d late
          </Badge>
        )}
        {planSummary.latest_supervisor_name && (
          <span className="text-xs text-slate-500">
            sup: <span className="text-slate-700">{planSummary.latest_supervisor_name}</span>
          </span>
        )}
      </div>

      {/* Plan history strip */}
      {planHistory.length > 0 && (
        <div className="mt-1 ml-5 flex flex-wrap gap-1.5">
          {planHistory.map((p) => {
            const isCancelled = p.status === 'cancelled';
            const cls = isCancelled
              ? 'line-through opacity-60 bg-rose-50 text-rose-700 border-rose-200'
              : `${getStatusPillClasses(p.status)}`;
            const tip = isCancelled
              ? `Cancelled (${p.cancellation_reason_code || 'n/a'}): ${p.cancellation_notes || ''}`
              : `Plan W${p.week_number}-${p.week_year} • ${formatDate(p.planned_start_date)} → ${formatDate(p.planned_completion_date)}`;
            return (
              <TooltipProvider key={p.plan_id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className={`text-xs px-1.5 py-0 cursor-help ${cls}`}>
                      W{p.week_number}-{String(p.week_year).slice(2)}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs max-w-[280px]">{tip}</div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div className="mt-1 ml-5 flex flex-wrap gap-1.5">
        {proc.needs_jc_issue && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => onIssueJC(wo, proc)}
            data-testid={`btn-issue-jc-${proc.process_id}`}
          >
            <FileText size={12} className="mr-1" />Issue JC
          </Button>
        )}
        {proc.pending_receive && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => onReceive(wo, proc)}
            data-testid={`btn-receive-process-${proc.process_id}`}
          >
            <CheckCircle2 size={12} className="mr-1" />Receive
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
          onClick={() => setShowJCs((v) => !v)}
          data-testid={`btn-view-progress-${proc.process_id}`}
        >
          {showJCs ? <ChevronDown size={12} className="mr-1" /> : <ChevronRight size={12} className="mr-1" />}
          {showJCs ? 'Hide' : 'View'} JC History
          <span className="ml-1 text-slate-400">({jobs.length})</span>
        </Button>
      </div>

      {/* Inline JC history table — read-only */}
      {showJCs && (
        <div className="mt-2 ml-5 border rounded-md bg-white overflow-x-auto" data-testid={`jc-history-${proc.process_id}`}>
          {jobs.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400 italic">No job cards issued yet for this process.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-2 py-1.5 font-medium">JC #</th>
                  <th className="text-left px-2 py-1.5 font-medium">Status</th>
                  <th className="text-right px-2 py-1.5 font-medium">Issued Qty</th>
                  <th className="text-right px-2 py-1.5 font-medium">Completed Qty</th>
                  <th className="text-left px-2 py-1.5 font-medium">Workers / Jobber</th>
                  <th className="text-left px-2 py-1.5 font-medium">Issued At</th>
                  <th className="text-left px-2 py-1.5 font-medium">Received At</th>
                  <th className="text-left px-2 py-1.5 font-medium">Issued By</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => {
                  const cancelled = j.status === 'cancelled' || j.status === 'partial_cancelled';
                  return (
                    <tr key={j.job_id} className={`border-t ${cancelled ? 'opacity-50 line-through' : ''}`}>
                      <td className="px-2 py-1.5 font-mono">{j.job_number || '—'}</td>
                      <td className="px-2 py-1.5">
                        <Badge variant="outline" className={`text-xs px-1.5 py-0 ${getStatusPillClasses(j.status)}`}>
                          {j.status}
                        </Badge>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{j.quantity_issued ?? '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{j.quantity_completed ?? '—'}</td>
                      <td className="px-2 py-1.5">
                        {j.is_outsourced ? (
                          <span className="text-violet-700">
                            OJC: {j.jobber_name || j.ojc_number || '—'}
                          </span>
                        ) : (
                          <span className="text-slate-700">
                            {(j.worker_names && j.worker_names.length > 0) ? j.worker_names.join(', ') : '—'}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-slate-600">{formatDate(j.issued_at)}</td>
                      <td className="px-2 py-1.5 text-slate-600">{formatDate(j.received_at)}</td>
                      <td className="px-2 py-1.5 text-slate-600">{j.issued_by_name || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// =====================================================================
// WO row (production mode) — collapsible processes
// =====================================================================
const WORow = ({ wo, onIssueJC, onReceive, onOpenWO }) => {
  const [open, setOpen] = useState(false);
  const isCombined = wo.is_combined_sfg;
  const completedPct = wo.wo_total_qty
    ? Math.round((wo.completed_for_this_so / Math.max(wo.qty_for_this_so, 1)) * 100)
    : 0;
  const toggle = () => setOpen((v) => !v);

  return (
    <div className="border rounded-md bg-white" data-testid={`wo-row-${wo.work_order_id}`}>
      <div className="flex items-center gap-2 p-2 hover:bg-slate-50">
        <div
          role="button"
          tabIndex={0}
          onClick={toggle}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
          className="flex-1 flex items-center gap-2 text-left cursor-pointer min-w-0"
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="font-mono text-xs text-slate-700">{wo.work_order_number}</span>
          <Badge variant="outline" className={`text-xs px-1.5 py-0 ${getStatusPillClasses(wo.wo_status)}`}>
            {wo.wo_status}
          </Badge>
          {isCombined && (
            <Badge variant="outline" className="text-xs px-1.5 py-0 bg-violet-50 text-violet-700 border-violet-200">
              <Layers size={11} className="mr-0.5" />combined
            </Badge>
          )}
          <span className="text-xs text-slate-500 tabular-nums">
            {wo.completed_for_this_so} / {wo.qty_for_this_so}
          </span>
          <div className="flex-1 mx-2 h-1 bg-slate-100 rounded overflow-hidden hidden md:block">
            <div className="h-1 bg-emerald-400" style={{ width: `${Math.min(completedPct, 100)}%` }} />
          </div>
          <SiblingChips siblings={wo.sibling_so_numbers || []} />
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs shrink-0"
          onClick={(e) => { e.stopPropagation(); onOpenWO(wo); }}
          data-testid={`btn-open-wo-${wo.work_order_id}`}
          title="Open WO detail (materials, BOM, processes)"
        >
          <ExternalLink size={12} className="mr-1" />Open WO
        </Button>
      </div>
      {open && (
        <div className="border-t bg-slate-50/50 p-2 space-y-1.5">
          {(wo.processes || []).map((p) => (
            <ProcessRow
              key={p.process_id}
              proc={p}
              wo={wo}
              onIssueJC={onIssueJC}
              onReceive={onReceive}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// =====================================================================
// Production-side line item card
// =====================================================================
const ProductionLineItem = ({ li, onIssueJC, onReceive, onOpenWO }) => {
  const dim = li.is_dispatched;
  return (
    <div
      className={`p-3 rounded-md border ${dim ? 'opacity-60 bg-slate-50' : 'bg-white'}`}
      data-testid={`prod-line-item-${li.line_item_id}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
        <span className="font-medium text-sm">{li.item_name}</span>
        <span className="font-mono text-xs text-slate-500">{li.item_code}</span>
        <span className="text-xs text-slate-600 tabular-nums">
          required: <span className="text-slate-900 font-medium">{li.required_qty}</span> ·
          {' '}produced: <span className="text-slate-900 font-medium">{li.produced_qty}</span> ·
          {' '}allocated: {li.allocated_qty} ·
          {' '}dispatched: {li.dispatched_qty}
        </span>
        {dim && (
          <Badge variant="outline" className="text-xs px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
            Dispatched
          </Badge>
        )}
        {li.badges?.bom_pending && <Badge variant="outline" className="text-xs px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">BOM Pending</Badge>}
        {li.badges?.wo_pending && <Badge variant="outline" className="text-xs px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">WO Pending</Badge>}
        {li.badges?.variant_pending && <Badge variant="outline" className="text-xs px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">Variant Pending</Badge>}
      </div>
      <div className="space-y-1.5">
        {(li.wos || []).length === 0 ? (
          <div className="text-xs text-slate-400 italic">No work orders yet for this line item.</div>
        ) : (
          (li.wos || []).map((wo) => (
            <WORow
              key={wo.work_order_id}
              wo={wo}
              onIssueJC={onIssueJC}
              onReceive={onReceive}
              onOpenWO={onOpenWO}
            />
          ))
        )}
      </div>
    </div>
  );
};

// =====================================================================
// Purchase-side requirement row
// =====================================================================
const RequirementRow = ({ req, onCreateIndent }) => {
  const dim = req.is_dispatched;
  const navigate = useNavigate();
  const backParams = `back=${encodeURIComponent('/purchase/pf-new')}&back_label=${encodeURIComponent('Purchase Fulfillment by SO')}`;
  const handleViewIndent = () => navigate(`/purchase/indents?indent=${encodeURIComponent(req.indent_number || '')}&${backParams}`);
  const handleViewPO = (po) => navigate(`/purchase/orders?po=${encodeURIComponent(po.po_number || '')}&${backParams}`);

  return (
    <div
      className={`p-2 rounded-md border ${dim ? 'opacity-60 bg-slate-50' : 'bg-white'}`}
      data-testid={`requirement-row-${req.indent_id}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-mono text-xs text-slate-700">{req.indent_number}</span>
        <Badge variant="outline" className={`text-xs px-1.5 py-0 ${getStatusPillClasses(req.indent_status)}`}>
          {req.indent_status}
        </Badge>
        <Badge variant="outline" className={`text-xs px-1.5 py-0 ${getStatusPillClasses(req.line_status)}`}>
          {req.line_status}
        </Badge>
        <span className="font-medium text-sm">{req.item_name}</span>
        <span className="font-mono text-xs text-slate-500">{req.item_code}</span>
        <span className="text-xs text-slate-600 tabular-nums">
          required: <span className="text-slate-900 font-medium">{req.required_qty}</span> ·
          {' '}ordered: {req.ordered_qty} · received: {req.received_qty}
        </span>
        {req.is_combined && (
          <Badge variant="outline" className="text-xs px-1.5 py-0 bg-violet-50 text-violet-700 border-violet-200">
            <Layers size={11} className="mr-0.5" />combined
          </Badge>
        )}
        {dim && (
          <Badge variant="outline" className="text-xs px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
            Dispatched
          </Badge>
        )}
        <SiblingChips siblings={req.sibling_so_numbers || []} />
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
          onClick={handleViewIndent}
          data-testid={`btn-view-indent-${req.indent_id}`}
        >
          <ExternalLink size={12} className="mr-1" />View Indent
        </Button>
        {(req.pos || []).map((po) => (
          <Button
            key={po.po_id}
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => handleViewPO(po)}
            data-testid={`btn-view-po-${po.po_id}`}
          >
            <ExternalLink size={12} className="mr-1" />
            {po.po_number} · {po.po_status}
          </Button>
        ))}
      </div>
    </div>
  );
};

// =====================================================================
// Purchase-side line item summary (with Create Indent action if pending)
// =====================================================================
const PurchaseLineItem = ({ li, parentSO, onCreateIndent }) => {
  const dim = li.is_dispatched;
  return (
    <div
      className={`p-2 rounded-md border ${dim ? 'opacity-60 bg-slate-50' : 'bg-white'}`}
      data-testid={`purch-line-item-${li.line_item_id}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium text-sm">{li.item_name}</span>
        <span className="font-mono text-xs text-slate-500">{li.item_code}</span>
        <span className="text-xs text-slate-600 tabular-nums">
          required: {li.required_qty} · dispatched: {li.dispatched_qty}
        </span>
        {dim && (
          <Badge variant="outline" className="text-xs px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
            Dispatched
          </Badge>
        )}
        {li.badges?.bom_pending && <Badge variant="outline" className="text-xs px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">BOM Pending</Badge>}
        {li.badges?.indent_pending && (
          <>
            <Badge variant="outline" className="text-xs px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">Indent Pending</Badge>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => onCreateIndent(li, parentSO)}
              data-testid={`btn-create-indent-${li.line_item_id}`}
            >
              <FileText size={12} className="mr-1" />Create Indent
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

// =====================================================================
// SO Card (collapsible) — common for both modes
// =====================================================================
const SOCard = ({ so, mode, onIssueJC, onReceive, onOpenWO, onCreateIndent }) => {
  const [open, setOpen] = useState(false);
  const dim = so.rollup?.fully_fulfilled;
  const pct = so.rollup?.pct ?? 0;
  const extraSubtitle = mode === 'production'
    ? `${so.rollup?.total_completed ?? 0} / ${so.rollup?.total_required ?? 0} produced`
    : `${so.rollup?.received_requirements ?? 0} of ${so.rollup?.total_requirements ?? 0} received`;

  return (
    <Card className={`${dim ? 'opacity-60' : ''}`} data-testid={`so-card-${so.so_number}`}>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex flex-wrap items-center gap-3 p-3 text-left hover:bg-slate-50/50"
          data-testid={`so-card-header-${so.so_number}`}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="font-mono text-sm font-semibold">{so.so_number}</span>
          <Badge variant="outline" className={`text-xs px-1.5 py-0 ${getStatusPillClasses(so.status)}`}>
            {so.status}
          </Badge>
          <span className="text-sm text-slate-700 truncate max-w-[280px]">{so.customer_name}</span>
          <span className="text-xs text-slate-500">delivery: {formatDate(so.delivery_date)}</span>
          <div className="flex-1" />
          <span className="text-xs text-slate-600 tabular-nums">{extraSubtitle}</span>
          <div className="w-24 h-1.5 bg-slate-100 rounded overflow-hidden">
            <div
              className={`h-1.5 ${dim ? 'bg-emerald-300' : 'bg-emerald-500'}`}
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
          <span className="text-xs font-semibold tabular-nums w-10 text-right">{pct}%</span>
        </button>

        {open && (
          <div className="border-t bg-slate-50/30 p-3 space-y-2">
            {mode === 'production' && (
              <>
                {(so.line_items || []).length === 0 && (
                  <div className="text-sm text-slate-400 italic">No line items.</div>
                )}
                {(so.line_items || []).map((li) => (
                  <ProductionLineItem
                    key={li.line_item_id}
                    li={li}
                    onIssueJC={onIssueJC}
                    onReceive={onReceive}
                    onOpenWO={onOpenWO}
                  />
                ))}
              </>
            )}
            {mode === 'purchase' && (
              <>
                <div className="space-y-1.5">
                  {(so.line_items || []).map((li) => (
                    <PurchaseLineItem
                      key={li.line_item_id}
                      li={li}
                      parentSO={so}
                      onCreateIndent={onCreateIndent}
                    />
                  ))}
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="text-xs font-medium text-slate-500 mb-1.5">Requirements</div>
                  {(so.requirements || []).length === 0 ? (
                    <div className="text-xs text-slate-400 italic">No purchase requirements yet.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {(so.requirements || []).map((r) => (
                        <RequirementRow key={r.indent_id} req={r} onCreateIndent={onCreateIndent} />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// =====================================================================
// Top-level component
// =====================================================================
export default function SOGroupedFulfillmentView({ mode }) {
  const navigate = useNavigate();
  const [sos, setSos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Sort key for the Pending Production tab (no effect on other tabs).
  const [pendingSortBy, setPendingSortBy] = useState('so_number');

  // Indent dialog (purchase)
  const [indentDialogOpen, setIndentDialogOpen] = useState(false);
  const [indentDialogItems, setIndentDialogItems] = useState([]);

  const endpoint = mode === 'production'
    ? '/api/production-fulfillment/by-so'
    : '/api/purchase-fulfillment/by-so';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}${endpoint}`, { headers: getAuthHeader() });
      setSos(r.data || []);
    } catch (e) {
      toast.error(`Failed to load: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  // Action handlers — production
  // Each deep-link includes back=<encoded src URL> + back_label=<friendly text>.
  // The destination page (WorkOrderPage / IndentRegister / PurchaseOrders) renders
  // a sticky "← Back to ..." button via <DeepLinkBackButton />.
  const backParams = mode === 'production'
    ? `&back=${encodeURIComponent('/production/pf-new')}&back_label=${encodeURIComponent('Production Fulfillment by SO')}`
    : `&back=${encodeURIComponent('/purchase/pf-new')}&back_label=${encodeURIComponent('Purchase Fulfillment by SO')}`;

  const handleIssueJC = (wo, proc) => {
    navigate(`/production/work-orders?wo=${encodeURIComponent(wo.work_order_id)}&action=issue-jc&process=${encodeURIComponent(proc.process_id)}${backParams}`);
  };
  const handleReceive = (wo, proc) => {
    const issuedJob = (proc.jobs || []).find((j) => j.status === 'issued');
    const jobIdParam = issuedJob ? `&job_id=${encodeURIComponent(issuedJob.job_id)}` : '';
    navigate(`/production/work-orders?wo=${encodeURIComponent(wo.work_order_id)}&action=receive-process&process=${encodeURIComponent(proc.process_id)}${jobIdParam}${backParams}`);
  };
  const handleOpenWO = (wo) => {
    // Lands on WODetailView (Level 2) via WorkOrderPage's render switch when ?wo=<id> is present without an action.
    navigate(`/production/work-orders?wo=${encodeURIComponent(wo.work_order_id)}${backParams}`);
  };

  // Action handler — purchase
  const handleCreateIndent = (li, parentSO) => {
    // Build dialog items[] using the existing IndentCreationDialog contract.
    // (See components/IndentCreationDialog.js — items[] with available_sos[].)
    const itemPayload = [{
      item_id: li.item_id,
      item_code: li.item_code,
      item_name: li.item_name,
      unit: 'NOS',
      source_type: 'MP',
      default_qty: Math.max((li.required_qty || 0) - (li.dispatched_qty || 0), 0),
      available_sos: [{ id: parentSO.sales_order_id, number: parentSO.so_number }],
      default_so_numbers: [parentSO.so_number],
    }];
    setIndentDialogItems(itemPayload);
    setIndentDialogOpen(true);
  };

  // Filtered list
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (sos || []).filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (!term) return true;
      const haystack = [
        s.so_number, s.customer_name, s.status,
        ...(s.line_items || []).map((li) => `${li.item_code} ${li.item_name}`),
      ].join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [sos, search, statusFilter]);

  // Distinct status list for filter
  const distinctStatuses = useMemo(() => {
    const set = new Set((sos || []).map((s) => s.status).filter(Boolean));
    return Array.from(set).sort();
  }, [sos]);

  // Pending Production tab — flat per-line rows.
  // Filter rule: required_qty > 0 AND produced_qty < required_qty.
  // Pending-BOM and pending-variant lines have produced_qty == 0 so they
  // qualify naturally — no special-casing.
  // Search term (if any) is applied at SO level using the same haystack as
  // the SO-accordion view, then lines from matching SOs are flattened.
  const pendingRows = useMemo(() => {
    if (statusFilter !== PENDING_PRODUCTION_TAB) return [];
    const term = search.trim().toLowerCase();
    const rows = [];
    for (const so of (sos || [])) {
      if (term) {
        const haystack = [
          so.so_number, so.customer_name, so.status,
          ...((so.line_items || []).map((li) => `${li.item_code} ${li.item_name}`)),
        ].join(' ').toLowerCase();
        if (!haystack.includes(term)) continue;
      }
      const tpd = resolveTargetProductionDate(so);
      for (const li of (so.line_items || [])) {
        const required = Number(li.required_qty || 0);
        const produced = Number(li.produced_qty || 0);
        if (required <= 0) continue;
        if (produced >= required) continue;
        rows.push({ so, line: li, tpd });
      }
    }
    // Sort
    if (pendingSortBy === 'target_production_date') {
      // Ascending; nulls (no target + no delivery_date) sort last.
      rows.sort((a, b) => {
        const av = a.tpd; const bv = b.tpd;
        if (av && bv) return av.localeCompare(bv);
        if (av && !bv) return -1;
        if (!av && bv) return 1;
        return compareSoNumbers(a.so.so_number, b.so.so_number);
      });
    } else {
      rows.sort((a, b) => compareSoNumbers(a.so.so_number, b.so.so_number));
    }
    return rows;
  }, [sos, search, statusFilter, pendingSortBy]);

  const handleExportPending = useCallback(() => {
    const rows = pendingRows.map(({ so, line }) => buildPendingProductionRow(so, line));
    if (rows.length === 0) {
      toast.info('Nothing to export');
      return;
    }
    exportToExcel(rows, 'Pending Production', `pending_production_${tsStamp()}.xlsx`);
  }, [pendingRows]);

  const isPendingTab = statusFilter === PENDING_PRODUCTION_TAB;

  return (
    <div className="space-y-3" data-testid={`so-grouped-view-${mode}`}>
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold">
          {mode === 'production' ? 'Production Fulfillment — by SO' : 'Purchase Fulfillment — by SO'}
        </h2>
        <Badge variant="outline" className="text-xs">
          {isPendingTab
            ? `${pendingRows.length} line${pendingRows.length === 1 ? '' : 's'}`
            : `${filtered.length} SO${filtered.length === 1 ? '' : 's'}`}
        </Badge>
        <Button
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          data-testid="btn-refresh-by-so"
        >
          <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        {isPendingTab && mode === 'production' && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleExportPending}
            disabled={loading || pendingRows.length === 0}
            data-testid="btn-export-pending-production"
          >
            <FileDown size={14} className="mr-1" />
            Export
          </Button>
        )}
        <div className="flex-1" />
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search SO / customer / item"
            className="pl-8 h-8 w-64 text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="input-so-search"
          />
        </div>
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="h-8">
            {mode === 'production' && (
              <TabsTrigger
                value={PENDING_PRODUCTION_TAB}
                className="text-xs px-2 h-7"
                data-testid="tab-pending-production"
              >
                Pending Production
              </TabsTrigger>
            )}
            <TabsTrigger value="all" className="text-xs px-2 h-7" data-testid="tab-status-all">All</TabsTrigger>
            {distinctStatuses.map((s) => (
              <TabsTrigger key={s} value={s} className="text-xs px-2 h-7" data-testid={`tab-status-${s}`}>
                {s.replace(/_/g, ' ')}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Body */}
      {loading ? (
        <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
      ) : isPendingTab ? (
        <>
          {/* Sort selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Sort by</span>
            <Select value={pendingSortBy} onValueChange={setPendingSortBy}>
              <SelectTrigger
                className="h-8 w-64 text-sm"
                data-testid="pending-sort-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="so_number">SO Number (ascending)</SelectItem>
                <SelectItem value="target_production_date">
                  Target Production Date (earliest first)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {pendingRows.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">
              No pending-production line items.
            </div>
          ) : (
            <div className="rounded border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {PENDING_PRODUCTION_COLUMNS.map((col) => (
                      <TableHead
                        key={col}
                        className="text-xs whitespace-nowrap"
                      >
                        {col}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingRows.map(({ so, line }, idx) => {
                    const row = buildPendingProductionRow(so, line);
                    return (
                      <TableRow
                        key={`${so.sales_order_id}:${line.line_item_id || idx}`}
                        data-testid="pending-production-row"
                      >
                        {PENDING_PRODUCTION_COLUMNS.map((col) => (
                          <TableCell
                            key={col}
                            className="text-xs whitespace-nowrap tabular-nums"
                          >
                            {row[col]}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center">No SOs match the current filter.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((so) => (
            <SOCard
              key={so.sales_order_id}
              so={so}
              mode={mode}
              onIssueJC={handleIssueJC}
              onReceive={handleReceive}
              onOpenWO={handleOpenWO}
              onCreateIndent={handleCreateIndent}
            />
          ))}
        </div>
      )}

      {/* Indent dialog (purchase only) */}
      {mode === 'purchase' && (
        <IndentCreationDialog
          open={indentDialogOpen}
          onOpenChange={setIndentDialogOpen}
          items={indentDialogItems}
          title="Create Indent"
          onSuccess={() => {
            setIndentDialogOpen(false);
            load();
            toast.success('Indent created');
          }}
        />
      )}
    </div>
  );
}
